import { useEffect, useSyncExternalStore } from "react";
import { KeyboardController, KeyboardEvents } from "@-/com.npmjs.react-native-keyboard-controller";

/**
 * Eager keyboard-visible store. Unlike `useKeyboardState` (which flips on
 * `keyboardWillShow` / `keyboardDidHide`), this flips on `keyboardWillHide`
 * too — so the dismiss button can unmount and the input can re-expand the
 * instant the dismiss animation STARTS, not 250ms later when it finishes.
 */
// eslint-disable-next-line no-top/no-top-level-side-effects -- module-level snapshot must match keyboard before listeners mount
let eagerKeyboardVisible = KeyboardController.isVisible();
const eagerKeyboardListeners = new Set<() => void>();

export function setEagerKeyboardVisible(next: boolean) {
  if (eagerKeyboardVisible === next) {
    return;
  }
  eagerKeyboardVisible = next;
  for (const listener of eagerKeyboardListeners) {
    listener();
  }
}

const EAGER_KEYBOARD_EVENTS = [
  ["keyboardWillShow", true],
  ["keyboardDidShow", true],
  ["keyboardWillHide", false],
  ["keyboardDidHide", false],
] as const;

export function useEagerKeyboardVisibleListeners() {
  useEffect(() => {
    const subscriptions = EAGER_KEYBOARD_EVENTS.map(([event, visible]) =>
      KeyboardEvents.addListener(event, () => {
        setEagerKeyboardVisible(visible);
      }),
    );

    setEagerKeyboardVisible(KeyboardController.isVisible());

    return () => {
      for (const subscription of subscriptions) {
        subscription.remove();
      }
    };
  }, []);
}

function subscribeEagerKeyboard(listener: () => void) {
  eagerKeyboardListeners.add(listener);
  return () => {
    eagerKeyboardListeners.delete(listener);
  };
}

function getEagerKeyboardVisible() {
  return eagerKeyboardVisible;
}

export function useEagerKeyboardVisible() {
  return useSyncExternalStore(
    subscribeEagerKeyboard,
    getEagerKeyboardVisible,
    getEagerKeyboardVisible,
  );
}
